import { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";

// Archive toggle at the top of the Edit Agency dialog's "Archive / Delete"
// tab. Archiving only moves the agency out of the main staff list into the
// collapsed "Archived agencies" list at the bottom of the page; logins,
// subscriptions, and data are untouched.
type Props = {
  agency: { id: string; name: string; archived_at?: string | null };
  adminEmail: string;
  onChanged: () => void;
};

const ArchiveAgencyPanel: React.FC<Props> = ({ agency, adminEmail, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const archived = !!agency.archived_at;

  const run = async () => {
    setBusy(true);
    try {
      await axios.patch(
        `/api/agency-management/agencies/${agency.id}/archive`,
        { adminEmail, archived: !archived },
        { headers: { "x-admin-email": adminEmail } }
      );
      toast.success(archived ? `Restored ${agency.name}` : `Archived ${agency.name}`);
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to update archive state");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-base-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm">
        <p className="m-0 font-semibold text-base-content">
          {archived ? "This agency is archived" : "Archive agency"}
        </p>
        <p className="m-0 mt-1 text-base-content/60">
          {archived
            ? `Archived ${new Date(agency.archived_at as string).toLocaleDateString()}. Restoring moves it back into the main list.`
            : "Hides it from the main list and moves it to the collapsed Archived list at the bottom of the page. Nothing is deleted and logins keep working."}
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={run} disabled={busy} loading={busy} className="px-1 md:px-4">
        {archived ? "Restore to list" : "Archive"}
      </Button>
    </div>
  );
};

export default ArchiveAgencyPanel;
